import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MapPin, Plus, Trash2, Pencil, Check, X } from 'lucide-react';

const LocationsManagement = () => {
  const [locations, setLocations] = useState([]);
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchLocations(); }, []);

  const fetchLocations = async () => {
    try {
      const res = await axios.get(`${API}/locations`);
      setLocations(res.data);
    } catch (e) { /* not admin */ }
  };

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) return;
    setSaving(true);
    try {
      await axios.post(`${API}/locations`, { name });
      toast.success('Location created');
      setNewName('');
      fetchLocations();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create location');
    } finally { setSaving(false); }
  };

  const beginEdit = (loc) => { setEditingId(loc.id); setEditName(loc.name); };
  const cancelEdit = () => { setEditingId(null); setEditName(''); };
  const saveEdit = async () => {
    if (!editName.trim()) return;
    try {
      await axios.put(`${API}/locations/${editingId}`, { name: editName.trim() });
      toast.success('Location renamed');
      setEditingId(null);
      fetchLocations();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to rename');
    }
  };

  const toggleActive = async (loc) => {
    try {
      await axios.put(`${API}/locations/${loc.id}`, { active: !loc.active });
      toast.success(loc.active ? 'Location deactivated' : 'Location activated');
      fetchLocations();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    }
  };

  const handleDelete = async (loc) => {
    if (!window.confirm(`Delete location "${loc.name}"?`)) return;
    try {
      await axios.delete(`${API}/locations/${loc.id}`);
      toast.success('Location deleted');
      fetchLocations();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <div data-testid="locations-management">
      <div className="flex items-center gap-2 mb-4">
        <MapPin className="w-5 h-5 text-[var(--color-primary)]" />
        <h3 className="text-base font-semibold">Locations</h3>
      </div>

      <div className="flex gap-2 mb-4">
        <Input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
          placeholder="New location name (e.g. Manchester Kitchen)"
          data-testid="location-new-name"
          className="flex-1"
        />
        <Button size="sm" className="btn-primary" onClick={handleCreate} disabled={saving || !newName.trim()} data-testid="location-add-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add
        </Button>
      </div>

      <div className="space-y-2">
        {locations.map(loc => (
          <div key={loc.id} className={`flex items-center justify-between p-3 border rounded-lg ${loc.active ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-100 opacity-70'}`} data-testid={`location-row-${loc.id}`}>
            <div className="flex items-center gap-3 flex-1">
              <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
                <MapPin className="w-4 h-4" />
              </div>
              {editingId === loc.id ? (
                <div className="flex items-center gap-2 flex-1">
                  <Input value={editName} onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit(); }}
                    className="h-8 max-w-sm" autoFocus />
                  <button onClick={saveEdit} className="p-1.5 text-green-600 hover:bg-green-50 rounded"><Check className="w-3.5 h-3.5" /></button>
                  <button onClick={cancelEdit} className="p-1.5 text-gray-400 hover:bg-gray-50 rounded"><X className="w-3.5 h-3.5" /></button>
                </div>
              ) : (
                <div>
                  <div className="font-medium text-sm">{loc.name}</div>
                  <div className="text-xs text-gray-400">ID: {loc.id}</div>
                </div>
              )}
            </div>
            {editingId !== loc.id && (
              <div className="flex items-center gap-2">
                <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold rounded ${loc.active ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                  {loc.active ? 'Active' : 'Inactive'}
                </span>
                <button onClick={() => beginEdit(loc)} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded"><Pencil className="w-3.5 h-3.5" /></button>
                <button onClick={() => toggleActive(loc)} className="px-2 py-1 text-[11px] border border-gray-200 rounded hover:bg-gray-50">
                  {loc.active ? 'Deactivate' : 'Activate'}
                </button>
                {loc.id !== 'main-kitchen' && (
                  <button onClick={() => handleDelete(loc)} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-3.5 h-3.5" /></button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default LocationsManagement;
